
export const chromaticScale = [
  'C', 'C#', 'D', 'Es', 'E', 'F', 'F#', 'G', 'G#', 'A', 'B', 'H',
];


const enharmonicAliases = {
  Db: 'C#',
  Eb: 'Es',
  Gb: 'F#',
  Ab: 'G#',
  As: 'G#',
  Bb: 'B',
  Cb: 'H',
};

// Semitone offsets from the root for each chord suffix
const intervalFormulas = {
  '': [0, 4, 7],
  maj: [0, 4, 7],
  m: [0, 3, 7],
  '7': [0, 4, 7, 10],
  m7: [0, 3, 7, 10],
  maj7: [0, 4, 7, 11],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  '°': [0, 3, 6],
  '°7': [0, 3, 6, 9],
  '+': [0, 4, 8],
  '+7': [0, 4, 8, 10],
  alt: [0, 4, 6, 8, 10],
  sus2sus4: [0, 2, 5, 7],
  '7sus4': [0, 5, 7, 10],
  '5': [0, 7],
  '6': [0, 4, 7, 9],
  '69': [0, 2, 4, 7, 9],
  '7b5': [0, 4, 6, 10],
  '9': [0, 2, 4, 7, 10],
  '9b5': [0, 2, 4, 6, 10],
  '+9': [0, 2, 4, 8, 10],
  '7b9': [0, 1, 4, 7, 10],
  '7#9': [0, 3, 4, 7, 10],
  '11': [0, 2, 4, 5, 7, 10],
  '9#11': [0, 2, 4, 6, 7, 10],
  '13': [0, 2, 4, 7, 9, 10],
  maj7b5: [0, 4, 6, 11],
  'maj7#5': [0, 4, 8, 11],
  maj9: [0, 2, 4, 7, 11],
  maj11: [0, 2, 4, 5, 7, 11],
  maj13: [0, 2, 4, 7, 9, 11],
  m6: [0, 3, 7, 9],
  m7b5: [0, 3, 6, 10],
  m9: [0, 2, 3, 7, 10],
  m69: [0, 2, 3, 7, 9],
  m11: [0, 2, 3, 5, 7, 10],
  mmaj7: [0, 3, 7, 11],
  mmaj7b5: [0, 3, 6, 11],
  mmaj9: [0, 2, 3, 7, 11],
  mmaj11: [0, 2, 3, 5, 7, 11],
  add9: [0, 2, 4, 7],
  madd9: [0, 2, 3, 7],
  add2: [0, 2, 4, 7],
};

const rootPattern = /^(Es|As|[A-GH][#b]?)(.*)$/;

const resolvePitchClass = note => {
  const normalized = enharmonicAliases[note] ?? note;
  const index = chromaticScale.indexOf(normalized);
  return index === -1 ? null : index;
};

export const parseChord = chordName => {
  const trimmed = (chordName ?? '').trim();
  if (trimmed.length === 0) return null;

  const parts = trimmed.split('/');
  const chordPart = parts[0];
  const bassPart = parts.length > 1 ? parts[1] : null;

  const match = rootPattern.exec(chordPart);
  if (match === null) return null;

  const rootPitchClass = resolvePitchClass(match[1]);
  if (rootPitchClass === null) return null;

  const suffix = match[2];
  const formula = intervalFormulas[suffix] ?? intervalFormulas.maj;
  const notePitchClasses = new Set(formula.map(interval => (rootPitchClass + interval) % 12));

  let bassPitchClass = null;
  if (bassPart) {
    const bassMatch = rootPattern.exec(bassPart);
    if (bassMatch !== null) {
      bassPitchClass = resolvePitchClass(bassMatch[1]);
    }
  }

  // A slash-chord bass note 
  if (bassPitchClass !== null) {
    notePitchClasses.add(bassPitchClass);
  }

  return {
    chordName: trimmed,
    rootPitchClass,
    notePitchClasses,
    bassPitchClass,
  };
};

// A slash chord's whole point is that the named bass note sounds lowest
const withBassLowest = (offsetsFromC, bassPitchClass) => {
  if (bassPitchClass === null || bassPitchClass === undefined) return offsetsFromC;

  const bassIndex = offsetsFromC.findIndex(o => (((o % 12) + 12) % 12) === bassPitchClass);
  if (bassIndex === -1) return offsetsFromC;

  const others = offsetsFromC.filter((_, i) => i !== bassIndex);
  if (others.length === 0) return offsetsFromC;

  const minOther = Math.min(...others);
  let bassOffset = offsetsFromC[bassIndex];
  while (bassOffset >= minOther) bassOffset -= 12;

  return offsetsFromC.map((o, i) => (i === bassIndex ? bassOffset : o));
};


const centeredVoicing = (chord, offsetsFromC) => {
  const lowest = Math.min(...offsetsFromC);
  const highest = Math.max(...offsetsFromC);
  const midpoint = (lowest + highest) / 2;

  let bestAnchor = null;
  let bestScore = Infinity;

  for (let anchor = lowest - 23; anchor <= lowest; anchor++) {
    const pitchClass = ((anchor % 12) + 12) % 12;
    if (pitchClass !== 0 && pitchClass !== 5) continue; // C or F only
    if (anchor + 23 < highest) continue; // window wouldn't reach the top note

    const score = Math.abs(anchor + 12 - midpoint);
    if (score < bestScore) {
      bestScore = score;
      bestAnchor = anchor;
    }
  }

  const windowStart = bestAnchor ?? lowest;
  const semitoneSpan = bestAnchor !== null ? 24 : highest - lowest + 1;

  return {
    chordName: chord.chordName,
    noteOffsets: new Set(offsetsFromC.map(offset => offset - windowStart)),
    rootPitchClass: chord.rootPitchClass,
    bassPitchClass: chord.bassPitchClass,
    windowStartPitchClass: ((windowStart % 12) + 12) % 12,
    semitoneSpan,
  };
};


export const getVoicings = (chord, maxVariations = 4) => {
  const relativeOffsets = [...chord.notePitchClasses]
    .map(pc => (pc - chord.rootPitchClass + 12) % 12)
    .sort((a, b) => a - b);

  const voicingCount = Math.min(Math.max(relativeOffsets.length, 0), maxVariations);
  const voicings = [];

  for (let inversion = 0; inversion < voicingCount; inversion++) {
    const offsetsFromC = relativeOffsets.map(
      (offset, j) => chord.rootPitchClass + offset + (j < inversion ? 12 : 0)
    );

    voicings.push(centeredVoicing(chord, withBassLowest(offsetsFromC, chord.bassPitchClass)));
  }

  return voicings;
};
